import type { Citation, CitationDictionaryData, FactSetCitationFqlMap } from "../types";
import { getCitationData, isDictionaryCitation, isMarkdownCitation } from "./findCitation";
import { extractHighlightIdsFromLinkText } from "./highlightIds";

const UUID_LIKE = /\bid="([0-9a-fA-F-]{8,})"|\bid='([0-9a-fA-F-]{8,})'/g;

export function extractIdsFromCitation(citation: Citation): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  const add = (id: string | undefined): void => {
    if (!id || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  };

  const fql = (citation.FQL ?? citation.fql) as FactSetCitationFqlMap | undefined;
  if (fql && typeof fql === "object") {
    for (const key of Object.keys(fql)) add(key);
  }

  const data = getCitationData(citation);
  if (isMarkdownCitation(citation) && typeof data === "string") {
    let match: RegExpExecArray | null;
    UUID_LIKE.lastIndex = 0;
    while ((match = UUID_LIKE.exec(data)) !== null) {
      add(match[1] ?? match[2]);
    }
  } else if (isDictionaryCitation(citation) && typeof data === "object" && data !== null) {
    for (const [key, link] of Object.entries(data as CitationDictionaryData)) {
      add(key);
      if (link?.text) {
        // Document (Dictionary) envelopes - SEC filings, web, broker research,
        // transcripts, etc. - carry their cited-value uuids in
        // data-highlight-ids, not inline id="...". Read both so these match the
        // model's declared citationIds in filterCitationPayloadByIds.
        for (const highlightId of extractHighlightIdsFromLinkText(link.text)) {
          add(highlightId);
        }
        let match: RegExpExecArray | null;
        UUID_LIKE.lastIndex = 0;
        while ((match = UUID_LIKE.exec(link.text)) !== null) {
          add(match[1] ?? match[2]);
        }
      }
    }
  }

  return ids;
}

export function buildCitationIndex(citations: Citation[]): Map<string, Citation> {
  const index = new Map<string, Citation>();
  for (const citation of citations) {
    const ids = extractIdsFromCitation(citation);
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      if (!index.has(id)) index.set(id, citation);
    }
  }
  return index;
}
